import { useState } from 'react';
import { useApp } from '../store';
import { couldRollBackTo, PULLED_LABEL, type Pull } from '../releases';
import type { Build } from '../types';

/**
 * TAKING A RELEASE BACK.
 *
 * Vinnie, 15 August, answering the question left open when releases were built:
 * *"yes, a release can be marked as withdrawn or rolled back."*
 *
 * TWO WORDS, BECAUSE THEY ARE TWO EVENTS. Withdrawn is a build taken out of
 * circulation with nothing put in its place. Rolled back is a build replaced by
 * an earlier one — which is a different fact for whoever is reading in a hurry,
 * because something is running out there and this says what.
 *
 * THE REASON IS THE POINT. At the moment a release is pulled everybody involved
 * already knows why, which is exactly why it is the field people would skip —
 * and in six months this line is the only record of it.
 */
export default function PullRelease({
  build,
  onDone,
  onCancel,
}: {
  build: Build;
  onDone: () => void;
  onCancel: () => void;
}) {
  const builds = useApp((s) => s.builds);
  const pullRelease = useApp((s) => s.pullRelease);

  const [kind, setKind] = useState<'withdrawn' | 'rolledback'>('withdrawn');
  const [why, setWhy] = useState('');
  const [back, setBack] = useState('');
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  const earlier = couldRollBackTo(build, builds);

  const save = async () => {
    setBusy(true);
    const pull: Pull = { kind, why, ...(kind === 'rolledback' && back ? { back } : {}) };
    let wrong = '';
    try {
      wrong = await pullRelease(build.id, pull);
    } catch (e) {
      wrong = (e as { message?: string })?.message || 'That could not be saved — try again.';
    } finally {
      setBusy(false);
    }
    setProblem(wrong);
    if (!wrong) onDone();
  };

  return (
    <div className="pull">
      <div className="grp-heading">
        Take back {build.version || `build ${build.build}`}
      </div>

      <div className="mode-cards">
        {(['withdrawn', 'rolledback'] as const).map((k) => (
          <button
            key={k}
            type="button"
            className={`mode-card ${kind === k ? 'is-on' : ''}`}
            onClick={() => setKind(k)}
          >
            <span className="mode-card-title">{PULLED_LABEL[k]}</span>
            <span className="mode-card-blurb">
              {k === 'withdrawn'
                ? 'Taken out of circulation, with nothing put in its place. Anybody who already installed it still has it.'
                : 'Replaced by an earlier version, which is what people are running now.'}
            </span>
          </button>
        ))}
      </div>

      {kind === 'rolledback' && (
        <label className="stack pull-back">
          Put back to
          {earlier.length ? (
            <select className="input" value={back} onChange={(e) => setBack(e.target.value)}>
              <option value="">Not recorded</option>
              {earlier.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          ) : (
            <span className="hint">
              No earlier release was recorded on this board, so there is nothing to name. The
              rollback still gets recorded.
            </span>
          )}
        </label>
      )}

      <label className="stack pull-why">
        Why it was taken back
        <input
          className="input"
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          placeholder="e.g. Sign-in failed on Android 14 for anybody upgrading"
          autoFocus
        />
      </label>
      <p className="hint">
        In six months this line is the only record of what happened. The version number stays
        used — the next release is a new one.
      </p>

      {problem && (
        <div className="login-error" role="alert">
          {problem}
        </div>
      )}

      <div className="grp-foot">
        <button
          className="btn btn-primary"
          disabled={busy || !why.trim()}
          title={!why.trim() ? 'Say why it was taken back' : undefined}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : `Mark it ${PULLED_LABEL[kind].toLowerCase()}`}
        </button>
        <button className="btn" onClick={onCancel}>
          Cancel
        </button>
        {!why.trim() && <span className="hint cut-needs">Needs a reason.</span>}
      </div>
    </div>
  );
}
